import React, { useState, useEffect } from 'react';
import { Users, Calendar, Download, RefreshCw, Mail, Phone, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VesakAdmin = () => {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const [regsResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/vesak-registration`),
        axios.get(`${API}/vesak-registration/count`)
      ]);
      
      setRegistrations(regsResponse.data);
      setStats(statsResponse.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Number of People', 'Morning Program', 'Evening Program', 'Special Requirements', 'Registered Date'];
    
    const rows = registrations.map(reg => [
      reg.name,
      reg.email,
      reg.phone,
      reg.numberOfPeople,
      reg.morningProgram ? 'Yes' : 'No',
      reg.eveningProgram ? 'Yes' : 'No',
      reg.specialRequirements || 'None',
      new Date(reg.registeredAt).toLocaleString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vesak-2026-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto text-amber-700 mb-4" size={48} />
          <p className="text-xl text-gray-700">Loading registrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-amber-900 mb-2">Vesak 2026 Registrations</h1>
          <p className="text-lg text-gray-700">Grand Opening Celebration - May 2, 2026</p>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card className="border-4 border-amber-400 bg-white shadow-xl">
              <CardContent className="p-6 text-center">
                <Users className="mx-auto text-amber-700 mb-2" size={32} />
                <div className="text-3xl font-bold text-amber-900 mb-1">{stats.totalRegistrations}</div>
                <div className="text-sm text-gray-600">Total Registrations</div>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-300 bg-white">
              <CardContent className="p-6 text-center">
                <Users className="mx-auto text-green-700 mb-2" size={32} />
                <div className="text-3xl font-bold text-green-900 mb-1">{stats.totalPeople}</div>
                <div className="text-sm text-gray-600">Total People Attending</div>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-300 bg-white">
              <CardContent className="p-6 text-center">
                <Calendar className="mx-auto text-orange-700 mb-2" size={28} />
                <div className="text-3xl font-bold text-orange-900 mb-1">{stats.morningProgram}</div>
                <div className="text-sm text-gray-600">Morning Program</div>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-300 bg-white">
              <CardContent className="p-6 text-center">
                <Calendar className="mx-auto text-purple-700 mb-2" size={28} />
                <div className="text-3xl font-bold text-purple-900 mb-1">{stats.eveningProgram}</div>
                <div className="text-sm text-gray-600">Evening Program</div>
              </CardContent>
            </Card>

            <Card className="border-2 border-blue-300 bg-white">
              <CardContent className="p-6 text-center">
                <Calendar className="mx-auto text-blue-700 mb-2" size={28} />
                <div className="text-3xl font-bold text-blue-900 mb-1">{stats.bothPrograms}</div>
                <div className="text-sm text-gray-600">Both Programs</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 mb-6">
          <Button 
            onClick={fetchRegistrations}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <RefreshCw className="mr-2" size={18} />
            Refresh
          </Button>
          <Button 
            onClick={exportToCSV}
            className="bg-green-600 hover:bg-green-700 text-white"
            disabled={registrations.length === 0}
          >
            <Download className="mr-2" size={18} />
            Export to CSV
          </Button>
        </div>

        {/* Registrations List */}
        <Card className="border-2 border-amber-300 shadow-xl">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-amber-900 mb-6">All Registrations ({registrations.length})</h2>
            
            {registrations.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto text-gray-300 mb-4" size={64} />
                <p className="text-xl text-gray-500">No registrations yet</p>
                <p className="text-gray-400 mt-2">Registrations will appear here when people sign up</p>
              </div>
            ) : (
              <div className="space-y-4">
                {registrations.map((reg, index) => (
                  <Card key={reg.id} className="border-2 border-gray-200 hover:border-amber-300 transition-all">
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Left Column */}
                        <div>
                          <div className="flex items-start gap-2 mb-3">
                            <User className="text-amber-700 mt-1 flex-shrink-0" size={18} />
                            <div>
                              <p className="font-bold text-lg text-amber-900">{index + 1}. {reg.name}</p>
                              <p className="text-sm text-gray-500">Registered: {new Date(reg.registeredAt).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2 mb-2">
                            <Mail className="text-gray-500 mt-1 flex-shrink-0" size={16} />
                            <a href={`mailto:${reg.email}`} className="text-sm text-gray-700 hover:text-amber-800 break-all">
                              {reg.email}
                            </a>
                          </div>
                          <div className="flex items-start gap-2">
                            <Phone className="text-gray-500 mt-1 flex-shrink-0" size={16} />
                            <a href={`tel:${reg.phone}`} className="text-sm text-gray-700 hover:text-amber-800">
                              {reg.phone}
                            </a>
                          </div>
                        </div>

                        {/* Middle Column */}
                        <div>
                          <div className="mb-3">
                            <p className="text-sm font-semibold text-gray-600 mb-1">Number of People</p>
                            <p className="text-2xl font-bold text-amber-900">{reg.numberOfPeople}</p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-600 mb-1">Programs</p>
                            <div className="flex flex-col gap-1">
                              {reg.morningProgram && (
                                <span className="inline-block bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">
                                  Morning (8 AM - 12 PM)
                                </span>
                              )}
                              {reg.eveningProgram && (
                                <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                                  Evening (5 PM - 9 PM)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right Column */}
                        <div>
                          <p className="text-sm font-semibold text-gray-600 mb-1">Special Requirements</p>
                          <p className="text-sm text-gray-700">
                            {reg.specialRequirements || <span className="text-gray-400 italic">None</span>}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VesakAdmin;
